import React from 'react';
import { Activity } from 'lucide-react';
import { formatShareCount } from '../../utils/ipoSubscription';
import { formatCount, formatTimes } from '../../utils/issueSubscription';

const timesTone = (times) => {
  if (times === null || times === undefined) return 'text-slate-400';
  if (times >= 1) return 'text-emerald-600';
  if (times >= 0.5) return 'text-amber-600';
  return 'text-rose-600';
};

const DemandBar = ({ times, scale }) => (
  <div className="w-16 sm:w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
    <div
      className={`h-full rounded-full ${times >= 1 ? 'bg-emerald-500' : 'bg-[#008080]'}`}
      style={{ width: `${Math.min(100, ((times || 0) / scale) * 100)}%` }}
    />
  </div>
);

const Row = ({ entry, scale, child = false }) => (
  <tr className={child ? 'bg-slate-50/60' : ''}>
    <th scope="row" className={`text-left font-semibold ${child ? 'pl-8 text-slate-500 text-[11px]' : 'pl-4 sm:pl-5 text-slate-800'} pr-3 py-2.5`}>
      {!child && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#008080] mr-2 align-middle" aria-hidden="true" />}
      {entry.label}
    </th>
    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{formatCount(entry.offered)}</td>
    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{formatCount(entry.bid)}</td>
    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
      {entry.amountCr === null ? '—' : entry.amountCr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </td>
    <td className={`px-3 py-2.5 text-right tabular-nums font-extrabold ${timesTone(entry.times)}`}>{formatTimes(entry.times)}</td>
    <td className="pl-3 pr-4 sm:pr-5 py-2.5">
      <DemandBar times={entry.times} scale={scale} />
    </td>
  </tr>
);

/**
 * NSE bid book: headline demand, progress to full subscription and the
 * category matrix. book: utils/issueSubscription.js mapBidBook
 */
export const SubscriptionBook = ({ book, phase, asOf, dayNumber, openOnLabel, status, error, onRetry }) => {
  const total = book?.total;
  const times = total?.times ?? 0;
  const scale = Math.max(1, ...(book?.categories || []).map((category) => category.times || 0));
  const isLive = phase === 'open' || phase === 'closing_today';
  const loading = !book?.hasBids && (status === 'idle' || status === 'loading');

  let emptyState = null;
  if (loading) emptyState = 'Loading the live bid book…';
  else if (status === 'failed' && !book?.hasBids) {
    emptyState = (
      <>
        The live bid book is temporarily unavailable{error ? ` (${error})` : ''}.{' '}
        <button type="button" onClick={onRetry} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!book?.hasBids && phase === 'upcoming') {
    emptyState = `Bidding opens on ${openOnLabel || 'the announced date'}. The live bid book appears here from 10:00 AM IST on day one.`;
  } else if (!book?.hasBids) {
    emptyState = isLive ? 'No bids have been recorded yet. Check back shortly.' : 'The exchange has no bid data for this symbol.';
  }

  return (
    <section aria-labelledby="bid-book-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div>
          <h2 id="bid-book-heading" className="flex items-center gap-2 text-sm sm:text-base font-extrabold text-slate-900">
            <Activity className="w-4 h-4 text-[#008080]" aria-hidden="true" />
            Live Subscription Book &amp; Quota Matrix
          </h2>
          <p className="mt-1 text-[11px] text-slate-500">
            {asOf ? `Bidding data as of ${asOf}` : 'Awaiting bidding data'}
            {dayNumber ? ` (Day ${dayNumber})` : ''} • Source: NSE bid book
          </p>
        </div>
        <div className="flex gap-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Shares Bid</p>
            <p className="text-base font-extrabold text-slate-900 tabular-nums">{book?.hasBids ? formatShareCount(total?.bid) : '—'}</p>
          </div>
          <div className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#006666]">Total Demand</p>
            <p className="text-base font-extrabold text-[#006666] tabular-nums">{book?.hasBids ? formatTimes(total?.times ?? null) : '—'}</p>
          </div>
        </div>
      </div>

      {emptyState ? (
        <p className="mx-4 sm:mx-5 mb-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          {emptyState}
        </p>
      ) : (
        <>
          <div className="px-4 sm:px-5 pb-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Cumulative Book: <span className="text-lg text-[#008080] normal-case tracking-normal font-extrabold">{formatTimes(times)}</span>{' '}
                <span className="normal-case tracking-normal font-medium text-slate-400">
                  ({formatCount(total?.bid)} shares bid vs {formatCount(total?.offered)} shares offered)
                </span>
              </p>
              <p className="text-[11px] font-semibold text-slate-500">
                Quota booked: <span className="font-extrabold text-slate-800">{Math.min(times * 100, 9999).toFixed(2)}%</span>
              </p>
            </div>
            <div
              className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden"
              role="progressbar"
              aria-label="Share of the public book subscribed"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(Math.min(times, 1) * 100)}
            >
              <div
                className={`h-full rounded-full ${times >= 1 ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#006666] to-[#14b8a6]'}`}
                style={{ width: `${Math.min(100, times * 100)}%` }}
              />
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-slate-400">
              <span>0.0x</span>
              <span>Full subscription: 1.00x ({formatShareCount(total?.offered)} shares)</span>
              <span>{times >= 1 ? 'Oversubscribed' : `${formatShareCount(Math.max(0, (total?.offered || 0) - (total?.bid || 0)))} shares to go`}</span>
            </div>
          </div>

          <div className="overflow-x-auto scrollbar-slim border-t border-slate-100">
            <table className="w-full text-xs sm:text-[13px] border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-slate-50/90 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="text-left pl-4 sm:pl-5 pr-3 py-2.5">Investor Category</th>
                  <th scope="col" className="text-right px-3 py-2.5">Shares Offered</th>
                  <th scope="col" className="text-right px-3 py-2.5">Shares Bid</th>
                  <th scope="col" className="text-right px-3 py-2.5">Bid Amount (₹ Cr)</th>
                  <th scope="col" className="text-right px-3 py-2.5">Times Subscribed</th>
                  <th scope="col" className="text-left pl-3 pr-4 sm:pr-5 py-2.5">Demand</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {book.categories.map((category) => (
                  <React.Fragment key={category.key}>
                    <Row entry={category} scale={scale} />
                    {category.children.map((child) => (
                      <Row key={child.key} entry={child} scale={scale} child />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              {total && (
                <tfoot>
                  <tr className="bg-slate-900 text-white">
                    <th scope="row" className="text-left pl-4 sm:pl-5 pr-3 py-3 font-extrabold">
                      Total (Ex-Anchor)
                    </th>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">{formatCount(total.offered)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">{formatCount(total.bid)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">
                      {total.amountCr === null ? '—' : total.amountCr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-extrabold text-teal-300">{formatTimes(total.times)}</td>
                    <td className="pl-3 pr-4 sm:pr-5 py-3 text-[11px] font-bold text-teal-100 whitespace-nowrap">
                      {isLive ? `Day ${dayNumber || 1} ongoing` : 'Final book'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="px-4 sm:px-5 py-3 border-t border-slate-100 flex flex-wrap gap-2 text-[11px]">
            {book.retailCutOffShare !== null && (
              <span className="px-2 py-1 rounded-md bg-teal-50 text-[#006666] font-semibold">
                {(book.retailCutOffShare * 100).toFixed(1)}% of retail bids at the cut-off price
              </span>
            )}
            {book.categories
              .filter((category) => category.key === 'qib' && category.breakdown.length)
              .flatMap((category) => category.breakdown)
              .map((entry) => (
                <span key={entry.label} className="px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-semibold">
                  QIB · {entry.label}: {formatCount(entry.bid)}
                </span>
              ))}
            <span className="text-slate-400 self-center">Bid amounts at the upper price band.</span>
          </div>
        </>
      )}
    </section>
  );
};

export default SubscriptionBook;
